/**
 * Minimal self-contained modal for the runtime shell — inline-styled so it
 * renders identically on any surface (the site viewer forces a light theme and
 * doesn't carry the app-shell CSS). Closes on Escape and backdrop click.
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { X } from 'lucide-react'

export default function ShellModal({
  open, onClose, title, children, width = 460,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  width?: number
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [open, onClose])

  if (!open) return null
  // Portal to <body> so the fixed overlay escapes the top bar's stacking
  // context AND its `backdrop-filter`, which would otherwise make the header
  // the containing block for position:fixed (clipping the modal to ~56px).
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(15,23,42,0.45)', display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center', padding: '4rem 1rem',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: width, background: '#fff', color: '#0f172a',
          borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', borderBottom: '1px solid #e2e8f0',
        }}>
          {/* Explicit colour: a host's global heading rule (the platform's dark
              theme) would otherwise paint the title light on this white card. */}
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: '#0f172a' }}>{title}</h2>
          <button
            type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex' }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: '1.25rem' }}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}

// Shared input + button styles, reused by the account + admin panels.
export const shellInput: React.CSSProperties = {
  padding: '0.5rem 0.65rem', border: '1px solid #cbd5e1', borderRadius: 8,
  fontSize: '0.9375rem', width: '100%', boxSizing: 'border-box', color: '#0f172a', background: '#fff',
}

export function shellBtn(kind: 'primary' | 'ghost' | 'danger' = 'primary'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '0.5rem 0.9rem', borderRadius: 8, fontWeight: 600, fontSize: '0.9125rem',
    cursor: 'pointer', border: '1px solid transparent', whiteSpace: 'nowrap',
  }
  if (kind === 'primary') return { ...base, background: '#4f46e5', color: '#fff' }
  if (kind === 'danger') return { ...base, background: '#fff', color: '#dc2626', borderColor: '#fecaca' }
  return { ...base, background: '#fff', color: '#475569', borderColor: '#e2e8f0' }
}
